import { useMemo } from 'react';
import { useTranslation } from '../../../i18n';
import { Tooltip } from '../../../components/Shared/Tooltip';
import type { TextSnippetCategory } from '../../../types/textSnippets';
import { getTokensForCategory } from '../../../utils/snippetTokens';
import { getSampleTokens } from '../../../utils/snippetSample';

interface TokenHelperProps {
  onInsert: (token: string) => void;
  /** Drives which tokens are offered — offer and invoice texts have different sets. */
  category: TextSnippetCategory;
}

export default function TokenHelper({ onInsert, category }: TokenHelperProps) {
  const { t } = useTranslation(['textSnippets', 'common']);
  const tokens = getTokensForCategory(category);
  const samples = useMemo(() => getSampleTokens(t), [t]);

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <p className="text-[13px] font-medium text-slate-900 mb-1">
        {t('textSnippets:tokens.title')}
      </p>
      <p className="text-[11px] text-slate-500 mb-3">
        {t('textSnippets:tokens.hint')}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {tokens.map((token) => (
          <Tooltip
            key={token}
            portal
            content={
              <span>
                {t(`textSnippets:tokens.${token}`)}
                <span className="text-slate-400"> · </span>
                <span className="font-mono-num">{samples[token]}</span>
              </span>
            }
          >
            <button
              type="button"
              onClick={() => onInsert(`{${token}}`)}
              aria-label={`${t(`textSnippets:tokens.${token}`)} · ${samples[token]}`}
              className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-mono-num bg-white border border-slate-200 text-slate-700 hover:border-[#00A871]/40 hover:bg-emerald-50 hover:text-[#00A871] transition-colors"
            >
              {`{${token}}`}
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}
